import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
  HttpResponse,
} from '@angular/common/http';
import { Injectable, inject } from '@angular/core';

import { NbToastrService } from '@nebular/theme';
import { NGXLogger } from 'ngx-logger';
import { Observable, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

import { AuthService } from '../../auth/services/auth.service';
import { TokenService } from '../../auth/services/token.service';
import { HIDE_ERRORS, HIDE_MESSAGES } from '../constants';

@Injectable()
export class ResponseCheckInterceptor implements HttpInterceptor {
  private toastrService = inject(NbToastrService);
  private authService = inject(AuthService);
  private tokenService = inject(TokenService);
  private logger = inject(NGXLogger);

  constructor() {
    this.logger.updateConfig({ ...this.logger.getConfigSnapshot(), context: 'INTERCEPTOR' });
  }

  intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    return next.handle(request).pipe(
      catchError((errorData: HttpErrorResponse) => {
        const { error, status } = errorData;
        let errorMsg = '';

        if (
          (status === 401 || error?.errorCode === '#ER-SY0002') &&
          error?.errorCode !== '#ER-SY0004' &&
          error?.errorCode !== '#ER-VA7074' &&
          error?.errorCode !== '#ER-SY0007'
        ) {
          this.authService.clearUserData();
          this.logger.warn('Authentication session timed out. Please log in again.');
          window.location.href = '/auth/login';
          return;
        }

        if (error?.message) {
          errorMsg = `Error: ${error.message}`;
        } else {
          errorMsg = `Error Code: ${error?.errorCode ?? status},  Message: ${error?.message || errorData.statusText}`;
        }

        if (error?.validationErrors) {
          for (const validationError of error.validationErrors) {
            this.toastrService.danger(validationError.message, '', {
              toastClass: 'no-title-toast',
            });
          }
        } else if (errorMsg) {
          let hiddenErrors: string[];
          if (request.context.get(HIDE_ERRORS)?.length) {
            hiddenErrors = request.context.get(HIDE_ERRORS);
            this.logger.warn('Hidden errors codes list:', hiddenErrors);
          }
          if (!hiddenErrors || !hiddenErrors?.includes(error?.errorCode)) {
            this.toastrService.danger(errorMsg, '', {
              toastClass: 'no-title-toast',
              duration: error?.errorCode === '#ER-SY0007' ? 0 : 15000,
            });
          }
        }

        return throwError(() => ({
          message: errorMsg,
          errorCode: error?.errorCode,
          originalMessage: error?.message || error?.error?.message,
          toString: () => errorMsg,
        }));
      }),
      map((event) => {
        if (event instanceof HttpResponse) {
          if (event.headers.has('set-authorization')) {
            this.logger.debug('A new token has been received');
            this.tokenService.updateToken(event.headers.get('set-authorization'));
          }
          const body = event.body;
          try {
            if (body?.userMessages) {
              const logLevel = {
                2: 'info',
                3: 'warning',
                4: 'danger',
              };
              let hiddenErrors: string[] = [];
              if (request.context.get(HIDE_ERRORS)?.length) {
                hiddenErrors = request.context.get(HIDE_ERRORS);
                this.logger.warn('Hidden errors codes list:', hiddenErrors);
              }
              for (const message of body.userMessages) {
                if (
                  message.errorCode === '#ER-VA7071' ||
                  message.errorCode === '#ER-VA7074' ||
                  hiddenErrors.includes(message.errorCode)
                ) {
                  continue;
                }
                if (message.level === 2 && request.context.get(HIDE_MESSAGES)) {
                  continue;
                }
                this.toastrService[logLevel[message.level]](message.message, '', {
                  toastClass: 'no-title-toast',
                  duration: 15000,
                });
              }
            }
          } catch (e) {
            this.logger.warn(e);
          }
        }
        return event;
      }),
    );
  }
}
